/**
 * SequentialMp4Player — plays a list of self-contained MP4 segments
 * back-to-back via a single `<video>` element by swapping `src` on
 * the `ended` event.
 *
 * Why this exists: V2 phone segments are non-fragmented MP4 with
 * moov at the end (each segment is independently playable, but the
 * shape isn't compatible with hls.js's CMAF or TS expectations).
 * The standard HLSPlayer can't load these. This component sidesteps
 * the HLS layer entirely — same pattern the `/api/p0` debug UI uses.
 *
 * Optional H.264 vs HEVC consideration: this player has no codec
 * preference of its own. The browser's `<video>` element decodes
 * whatever the MP4 contains. With the encoder flipped to AVC (Phase
 * 2.B encoder-pref change shipped alongside this) every browser
 * plays the result.
 *
 * Scope: minimal "watch from start to end" surface. No multi-camera
 * sync, no UTC-anchored seekToUtc, no HLS live-edge tailing. That's
 * intentional — the share-link use case is "guest opens a link and
 * watches", which fits a simple sequential player. Operator-side
 * synced-viewer / map / IMU sync continues to use HLSPlayer and
 * still works on browsers with HEVC support; share is what was
 * uniquely broken.
 */

import React, { useEffect, useRef, useState } from 'react';

export interface SequentialMp4PlayerProps {
  /** Absolute segment URLs to play in order. */
  segmentUrls: string[];
  /** Label rendered above the player; optional. */
  label?: string | null;
  /** Fired when playback reaches the end of the final segment. */
  onAllEnded?: () => void;
  /** Autoplay when the first source loads. Default true; respects
   * browser autoplay policy (will fail silently if blocked, the
   * user can hit play). */
  autoPlay?: boolean;
}

export const SequentialMp4Player: React.FC<SequentialMp4PlayerProps> = ({
  segmentUrls,
  label,
  onAllEnded,
  autoPlay = true,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [index, setIndex] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIndex(0);
    setError(null);
  }, [segmentUrls]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || segmentUrls.length === 0) return;
    if (index >= segmentUrls.length) {
      if (onAllEnded) onAllEnded();
      return;
    }
    video.src = segmentUrls[index];
    if (autoPlay) {
      video.play().catch(() => undefined);
    }
  }, [index, segmentUrls, autoPlay, onAllEnded]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onEnded = () => {
      setIndex((i) => Math.min(i + 1, segmentUrls.length));
    };
    const onMediaError = () => {
      const code = video.error?.code;
      const msg =
        code === 4
          ? 'codec not supported by this browser (try Safari or a Chromium with HEVC)'
          : code === 3
            ? 'decode error in segment'
            : code === 2
              ? 'network error fetching segment'
              : 'playback error';
      setError(msg);
    };
    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onMediaError);
    return () => {
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onMediaError);
    };
  }, [segmentUrls.length]);

  if (segmentUrls.length === 0) {
    return <div className="p-4 font-mono text-fg-muted">no segments to play</div>;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="mb-1 flex items-center gap-2">
        {label && <span className="font-mono text-xs text-fg-secondary">{label}</span>}
        <div className="flex-1" />
        <span className="font-mono text-xs text-fg-muted">
          segment {Math.min(index + 1, segmentUrls.length)} / {segmentUrls.length}
        </span>
      </div>
      {error && <p className="mb-1 font-mono text-xs text-status-error">{error}</p>}
      <video
        ref={videoRef}
        controls
        playsInline
        muted={autoPlay}
        className="h-full min-h-[360px] w-full rounded bg-bg-video"
      />
    </div>
  );
};

export default SequentialMp4Player;
